import {
  FontOption,
  PPT_MAKER_THEMES,
  ThemeType,
  FONTS,
  THEME_OPTIONS,
  SlideType,
} from '@lessonPlanner/constants';

import { ComboboxField } from '../form-fields';
import {
  ThemeOption,
  ThemeSelectorField,
} from '../form-fields/ThemeSelectorField';
import { DownloadDropdown } from '../molecules/DownloadDropdown';
import { PptSlideContent } from '../molecules/PptSlideContent';
import { PptSlidePreviewPanels } from '../molecules/PptSlidePreviewPanels';

import { PptSlide } from '../../types/ppt-slide';
import {
  getLayoutBackgroundUrl,
  clearAllLayoutCaches,
} from '../../utils/slideBackgroundHelper';

type PptFont = (typeof FONTS)[number];
type PptTheme = (typeof THEME_OPTIONS)[number];
type PptContentVariations = string | Record<string, string>;
enum PptBackgroundImageKey {
  TITLE = 'backgroundImageTitle',
  CONTENT = 'backgroundImageContent',
}
interface PptSlidesProps {
  slides: PptSlide[];
  currentSlide: number;
  setCurrentSlide: (value: number | ((prev: number) => number)) => void;
  handleClickDownload: (isPPTXDownload: boolean) => void;
  selectedFont: PptFont;
  selectedTheme: PptTheme;
  setSelectedFont: (value: PptFont) => void;
  setSelectedTheme: (value: PptTheme) => void;
}

export function PptSlides({
  slides,
  currentSlide,
  setCurrentSlide,
  handleClickDownload,
  selectedFont,
  selectedTheme,
  setSelectedTheme,
  setSelectedFont,
}: PptSlidesProps) {
  const fallbackTitle = 'Here&apos;s Your Presentation!';
  const THEMES_TOOLTIP =
    'Choose a theme with a color palette that matches your presentation';
  const FONTS_TOOLTIP = 'Choose a font style for your presentation';

  if (!slides || slides.length === 0) {
    return null;
  }

  function getSlideStyles(
    theme: (typeof THEME_OPTIONS)[0],
    slideType: SlideType,
    slideIndex?: number,
  ) {
    const themeColors = PPT_MAKER_THEMES[theme.id];
    const textColor =
      slideType === SlideType.TITLE
        ? themeColors.titleText ?? themeColors.text
        : themeColors.contentText ?? themeColors.text;

    return {
      backgroundColor: PPT_MAKER_THEMES[theme.id].background,
      backgroundImage: getBackgroundImageUrl(theme, slideType, slideIndex)
        ? `url(${getBackgroundImageUrl(theme, slideType, slideIndex)})`
        : 'none',
      color: textColor,
      fontFamily: selectedFont.name,
      fontSize: 'clamp(8px, 2vw, 18px)',
      overflow: 'hidden',
    };
  }

  function getBackgroundImageUrl(
    theme: (typeof THEME_OPTIONS)[0],
    slideType: SlideType,
    slideIndex?: number,
  ): string {
    if (slideType === SlideType.TITLE) {
      return (
        (PPT_MAKER_THEMES[theme.id][PptBackgroundImageKey.TITLE] as string) ??
        ''
      );
    }

    if (slideIndex !== undefined) {
      const currentSlide = slides[slideIndex];

      if (currentSlide?.layout) {
        return getLayoutBackgroundUrl(theme.id, slideIndex, slides) || '';
      }

      if (
        theme.id === ThemeType.WATER_COLOR ||
        theme.id === ThemeType.STATIONERY ||
        theme.id === ThemeType.ENERGY ||
        theme.id === ThemeType.LABORATORY ||
        theme.id === ThemeType.CIRCLE_OF_LIFE ||
        theme.id === ThemeType.SPACE ||
        theme.id === ThemeType.WEATHER ||
        theme.id === ThemeType.OFFICE ||
        theme.id === ThemeType.EVERGREEN ||
        theme.id === ThemeType.GRADIENT
      ) {
        return getLayoutBackgroundUrl(theme.id, slideIndex, slides) || '';
      }
    }

    return getContentBackgroundVariation(
      PPT_MAKER_THEMES[theme.id][PptBackgroundImageKey.CONTENT],
      slideIndex,
    );
  }

  function getContentBackgroundVariation(
    contentVariations: PptContentVariations,
    slideIndex?: number,
  ): string {
    if (typeof contentVariations === 'string') {
      return contentVariations;
    }
    if (contentVariations && typeof contentVariations === 'object') {
      const variationKeys = Object.keys(contentVariations);
      const totalVariations = variationKeys.length;
      if (totalVariations > 0 && slideIndex !== undefined) {
        const variationIndex = Math.floor(
          (slideIndex / slides.length) * totalVariations,
        );
        const key =
          variationKeys[Math.min(variationIndex, totalVariations - 1)];
        return contentVariations[key] ?? '';
      }
      return contentVariations['variant1'] ?? '';
    }
    return '';
  }

  function renderHeaders() {
    return (
      <div className="flex w-full flex-wrap items-center justify-between gap-2 sm:gap-4">
        <div className="flex flex-row items-end justify-start gap-2.5">
          <span className="text-denimBlue min-w-0 flex-1 break-words text-xl font-semibold sm:text-2xl">
            {slides[0]?.title || fallbackTitle}
          </span>
          <span className="flex-shrink-0 rounded-md bg-gray-200 px-2 py-1 text-xs font-medium text-gray-600 sm:text-sm">
            Total Slides: {slides.length}
          </span>
        </div>
      </div>
    );
  }

  function renderToolbar() {
    return (
      <div className="flex flex-wrap items-center justify-between gap-5 sm:gap-3">
        <div className="grid grid-cols-1 items-center gap-6 sm:grid-cols-2 w-full sm:w-auto">
          <ComboboxField
            highlight
            className="w-full sm:w-64"
            label="Font"
            options={FONTS}
            placeholder="Select a font"
            tooltip={FONTS_TOOLTIP}
            value={selectedFont}
            onChange={(newFont: { id: FontOption; name: FontOption }) => {
              setSelectedFont(newFont);
            }}
          />

          <ThemeSelectorField
            highlight
            label="Theme"
            options={THEME_OPTIONS as ThemeOption[]}
            slideTitle={slides[currentSlide]?.title || fallbackTitle}
            tooltip={THEMES_TOOLTIP}
            value={selectedTheme as ThemeOption}
            className={{
              button: 'w-full sm:w-56',
            }}
            onChange={(newTheme) => {
              clearAllLayoutCaches();

              setSelectedTheme({
                ...newTheme,
                id: newTheme.id as ThemeType,
                background: newTheme.background as string,
                text: newTheme.text as string,
                backgroundImageTitle: newTheme.backgroundImageTitle as string,
                backgroundImageContent: newTheme.backgroundImageContent,
              });
            }}
          />
        </div>
        <DownloadDropdown onDownload={handleClickDownload} />
      </div>
    );
  }

  return (
    <div className="relative flex grow flex-col">
      <div className="mb-4 flex flex-col gap-4">
        {renderHeaders()}
        {renderToolbar()}
      </div>
      <div className="flex h-full grow flex-col gap-4">
        <div className="flex justify-center">
          <PptSlideContent
            currentSlide={currentSlide}
            selectedFont={selectedFont}
            selectedTheme={selectedTheme}
            setCurrentSlide={setCurrentSlide}
            slides={slides}
            titleSlideStyles={getSlideStyles(selectedTheme, SlideType.TITLE)}
            updateCurrentSlide={setCurrentSlide}
            contentSlideStyles={getSlideStyles(
              selectedTheme,
              SlideType.CONTENT,
              currentSlide,
            )}
          />
        </div>

        <div className="w-full overflow-x-auto">
          <div className="flex gap-4 pb-2">
            <PptSlidePreviewPanels
              currentSlide={currentSlide}
              getSlideStyles={getSlideStyles}
              selectedFont={selectedFont}
              selectedTheme={selectedTheme}
              setCurrentSlide={setCurrentSlide}
              slides={slides}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
